
import { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import routes from '../../router/config';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAreasOpen, setIsAreasOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  // Automatically generate area pages from routes
  const areaPages = useMemo(() => {
    return routes
      .filter(route => route.path?.startsWith('/areas/'))
      .map(route => {
        const path = route.path!;
        const name = path
          .replace('/areas/', '')
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return { name, href: path };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const servicePages = [
    { name: 'All Services', href: '/services' },
    { name: 'Interior Painting', href: '/services/interior-painting' },
    { name: 'Exterior Painting', href: '/services/exterior-painting' },
    { name: 'Commercial Painting', href: '/services/commercial-painting' },
    { name: 'Roof Painting', href: '/services/roof-painting' },
    { name: 'Waterproofing', href: '/services/waterproofing' },
  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
      <nav className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                <i className="ri-brush-line text-white text-xl"></i>
              </div>
              <span className="text-2xl font-bold text-black tracking-tight">Cape Town Painters</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:block">
            <div className="flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    location.pathname === item.href
                      ? 'text-black'
                      : 'text-gray-600 hover:text-black'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Services Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  onMouseEnter={() => setIsServicesOpen(true)}
                  className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center space-x-1 ${
                    location.pathname.startsWith('/services')
                      ? 'text-black'
                      : 'text-gray-600 hover:text-black'
                  }`}
                >
                  <span>Services</span>
                  <i className={`ri-arrow-${isServicesOpen ? 'up' : 'down'}-s-line text-sm`}></i>
                </button>
                
                {isServicesOpen && (
                  <div 
                    className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50"
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    {servicePages.map((service) => (
                      <Link
                        key={service.name}
                        to={service.href}
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-black transition-colors cursor-pointer"
                        onClick={() => setIsServicesOpen(false)}
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Service Areas Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsAreasOpen(!isAreasOpen)}
                  onMouseEnter={() => setIsAreasOpen(true)}
                  className={`text-base font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center space-x-1 ${
                    location.pathname.startsWith('/areas')
                      ? 'text-black'
                      : 'text-gray-600 hover:text-black'
                  }`}
                >
                  <span>Service Areas</span>
                  <i className={`ri-arrow-${isAreasOpen ? 'up' : 'down'}-s-line text-sm`}></i>
                </button>
                
                {isAreasOpen && (
                  <div 
                    className="absolute top-full right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50 max-h-96 overflow-y-auto"
                    onMouseLeave={() => setIsAreasOpen(false)}
                  >
                    {areaPages.map((area) => (
                      <Link
                        key={area.name}
                        to={area.href}
                        className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-black transition-colors cursor-pointer"
                        onClick={() => setIsAreasOpen(false)}
                      >
                        {area.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              className="bg-yellow-400 text-black px-6 py-3 rounded-lg text-base font-semibold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer shadow-sm"
            >
              Get Quote
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-black hover:bg-gray-100 cursor-pointer"
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-100 max-h-[80vh] overflow-y-auto">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-3 rounded-md text-base font-medium transition-colors cursor-pointer ${
                    location.pathname === item.href
                      ? 'bg-gray-100 text-black'
                      : 'text-gray-600 hover:text-black hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Services Section */}
              <div className="border-t border-gray-100 pt-3 mt-3">
                <div className="px-3 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wide">Services</div>
                {servicePages.map((service) => (
                  <Link
                    key={service.name}
                    to={service.href}
                    className="block px-3 py-3 rounded-md text-base font-medium text-gray-600 hover:text-black hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>
              
              {/* Mobile Service Areas Section */}
              <div className="border-t border-gray-100 pt-3 mt-3">
                <div className="px-3 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wide">Service Areas</div>
                <div className="space-y-1">
                  {areaPages.map((area) => (
                    <Link
                      key={area.name}
                      to={area.href}
                      className="block px-3 py-3 rounded-md text-base font-medium text-gray-600 hover:text-black hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {area.name}
                    </Link>
                  ))}
                </div>
              </div>
              
              <Link
                to="/contact"
                className="block w-full text-center bg-yellow-400 text-black px-4 py-3 rounded-lg text-base font-semibold hover:bg-yellow-500 transition-colors mt-4 cursor-pointer"
                onClick={() => setIsMenuOpen(false)}
              >
                Get Quote
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
